import React from 'react';
import { motion } from 'framer-motion';
import ParticleBackground from '../components/ParticleBackground';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Seo from '../components/Seo';

export default function Chat({ meta_tags }) {
  return (
    <>
      <Seo meta={meta_tags} />

      <div className="bg-gradient-to-b from-indigo-950 via-purple-900 to-indigo-950 min-h-screen relative overflow-hidden flex flex-col">
      <ParticleBackground starCount={30} particleCount={15} />
      <Header />

      <div className="relative z-10 flex-1 py-12 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Hero Section */}
          <motion.div
            className="text-center mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Animated Icon */}
            <motion.div
              className="inline-block mb-6"
              animate={{
                rotate: [0, 5, -5, 0],
                scale: [1, 1.1, 1],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            >
              <div className="relative">
                <span className="text-7xl drop-shadow-[0_0_20px_rgba(251,191,36,0.5)]">💬</span>
                <motion.div
                  className="absolute inset-0 blur-xl bg-amber-500/30 rounded-full"
                  animate={{
                    scale: [1, 1.2, 1],
                    opacity: [0.3, 0.6, 0.3],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                  }}
                />
              </div>
            </motion.div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-heading font-bold mb-6">
              <span className="bg-gradient-to-r from-amber-200 via-purple-300 to-pink-300 bg-clip-text text-transparent drop-shadow-lg">
                Chat with Tarot Reader AI
              </span>
            </h1>

            <p className="text-xl md:text-2xl text-purple-100 mb-4 max-w-3xl mx-auto leading-relaxed">
              Have a conversation with our AI tarot reader. Ask questions, get guidance, and explore your path.
            </p>

            <div className="flex items-center justify-center gap-3 text-amber-200">
              <span className="text-2xl">✨</span>
              <p className="text-lg font-semibold">
                100% Free • Instant Responses • No Signup Required
              </p>
              <span className="text-2xl">✨</span>
            </div>
          </motion.div>

          {/* Chat Interface */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="max-w-5xl mx-auto"
          >
            <div className="relative">
              {/* Glowing border effect */}
              <motion.div
                className="absolute -inset-1 bg-gradient-to-r from-amber-500 via-purple-500 to-pink-500 rounded-3xl blur opacity-30"
                animate={{
                  opacity: [0.2, 0.4, 0.2],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                }}
              />

              <div className="relative bg-gradient-to-br from-purple-900/90 to-indigo-900/90 backdrop-blur-xl rounded-3xl border-2 border-purple-400/30 shadow-2xl overflow-hidden">
                <iframe
                  id="iframe-chat"
                  className="w-full h-[600px] md:h-[700px]"
                  src="https://www.chatbotgen.com/v2/chatbots/tarot-reader-ai/widget"
                  frameBorder="0"
                  spellCheck="false"
                  aria-label="Chat with Tarot Reader AI"
                  title="Tarot Reader AI Chat"
                />
              </div>
            </div>
          </motion.div>

          {/* Info Section */}
          <motion.div
            className="mt-12 text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl font-heading font-bold text-transparent bg-gradient-to-r from-amber-200 to-purple-300 bg-clip-text mb-4">
                How to Use the Chat
              </h2>
              <div className="grid md:grid-cols-3 gap-6 text-left">
                <div className="bg-purple-900/40 backdrop-blur rounded-xl p-6 border border-purple-400/20">
                  <div className="text-3xl mb-3">💭</div>
                  <h3 className="font-heading font-bold text-amber-200 mb-2">1. Ask Freely</h3>
                  <p className="text-purple-200 text-sm">
                    Type your questions naturally. Ask about love, career, or any area of life.
                  </p>
                </div>
                <div className="bg-purple-900/40 backdrop-blur rounded-xl p-6 border border-purple-400/20">
                  <div className="text-3xl mb-3">🎴</div>
                  <h3 className="font-heading font-bold text-amber-200 mb-2">2. Get Insights</h3>
                  <p className="text-purple-200 text-sm">
                    Receive personalized guidance and tarot wisdom in real-time conversation.
                  </p>
                </div>
                <div className="bg-purple-900/40 backdrop-blur rounded-xl p-6 border border-purple-400/20">
                  <div className="text-3xl mb-3">🌟</div>
                  <h3 className="font-heading font-bold text-amber-200 mb-2">3. Explore Deeper</h3>
                  <p className="text-purple-200 text-sm">
                    Ask follow-up questions to dive deeper into your reading and guidance.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* CTA Section */}
          <motion.div
            className="text-center mt-12"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <p className="text-purple-200 mb-4">
              Want a traditional card-based reading?
            </p>
            <a
              href="/"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500 via-purple-500 to-pink-500 text-white px-8 py-4 rounded-full text-lg font-bold hover:shadow-[0_0_40px_rgba(251,191,36,0.8)] transition-all"
            >
              <span>🔮</span>
              Get a Tarot Reading
            </a>
          </motion.div>
        </div>
      </div>

      <Footer />
      </div>
    </>
  );
}
